import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

// Inspektionsarten
const INSPECTION_TYPES = [
  { key: 'uvvInspection', label: 'UVV' },
  { key: 'tuvInspection', label: 'TÜV' },
  { key: 'tachoInspection', label: 'Tacho' },
  { key: 'asuInspection', label: 'ASU' },
  { key: 'otherInspection', label: 'Sonstige' },
];

// Artikeltypen
const ITEM_TYPES = [
  { value: 'rental', label: 'Mietartikel' },
  { value: 'sale', label: 'Verkauf' },
  { value: 'service', label: 'Service' },
  { value: 'sparePart', label: 'Ersatzteil' },
  
  { value: 'transportvehicle', label: 'Transportfahrzeug' },
  { value: 'inventory', label: 'Inventar' },
];

function InspectionForm() {
  const today = new Date();
  const monthLater = new Date(today);
  monthLater.setDate(today.getDate() + 30);

  const [dateFrom, setDateFrom] = useState(today.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(monthLater.toISOString().slice(0, 10));
  const [selectedTypes, setSelectedTypes] = useState(INSPECTION_TYPES.map(t => t.key));
  const [selectedItemTypes, setSelectedItemTypes] = useState(ITEM_TYPES.map(t => t.value));
  const [storageLocations, setStorageLocations] = useState([]);
  const [selectedStorageLocation, setSelectedStorageLocation] = useState('all');
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchStorageLocations() {
      try {
        const { data } = await api.get('/storage-locations');
        setStorageLocations(data || []);
      } catch (e) {
        console.error('Fehler beim Laden der Lagerorte:', e);
      }
    }
    fetchStorageLocations();
  }, []);

  // Checkbox-Handling Inspektionstypen
  const handleInspectionTypeChange = (key) => {
    setSelectedTypes((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  // Checkbox-Handling Itemtypen
  const handleItemTypeChange = (value) => {
    setSelectedItemTypes((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  // Daten holen
  useEffect(() => {
    async function fetchInspections() {
      setLoading(true);
      setError('');
      try {
        // Wenn keine Itemtypen ausgewählt: Zeige nichts
        if (selectedItemTypes.length === 0) {
          setInspections([]);
          setLoading(false);
          return;
        }
        // Items filtern nach Typen
        const itemParams = { itemTypes: selectedItemTypes.join(',') };
        if (selectedStorageLocation !== 'all') {
          itemParams.storageLocationId = selectedStorageLocation;
        }

        const { data: items } = await api.get('/items', {
          params: itemParams
        });
        let inspectionRows = [];
        for (const item of items) {
          INSPECTION_TYPES.forEach(({ key, label }) => {
            if (
              selectedTypes.includes(key) &&
              item[key] &&
              item[key] >= dateFrom &&
              item[key] <= dateTo
            ) {
              inspectionRows.push({
                inspectionType: label,
                inspectionDate: item[key],
                ...item,
              });
            }
          });
        }
        inspectionRows.sort((a, b) => (a.inspectionDate > b.inspectionDate ? 1 : -1));
        setInspections(inspectionRows);
      } catch (e) {
        setError('Inspektionen konnten nicht geladen werden.');
        setInspections([]);
      } finally {
        setLoading(false);
      }
    }
    fetchInspections();
  }, [dateFrom, dateTo, selectedTypes, selectedItemTypes, selectedStorageLocation]);

  const handleDownloadPDF = () => {
  if (!dateFrom || !dateTo) {
    setError('Bitte einen gültigen Datumsbereich auswählen!');
    return;
  }
  if (!selectedTypes.length) {
    setError('Bitte mindestens einen Inspektionstyp auswählen!');
    return;
  }
  if (!selectedItemTypes.length) {
    setError('Bitte mindestens einen Artikeltyp auswählen!');
    return;
  }

  // PDF-URL bauen (alle Filter berücksichtigen)
  const baseURL = api.defaults.baseURL;
  const url = `${baseURL}/items/inspectionReport/pdf?dateFrom=${dateFrom}&dateTo=${dateTo}`
    + `&inspectionTypes=${selectedTypes.join(',')}`
    + `&itemTypes=${selectedItemTypes.join(',')}`
    + (selectedStorageLocation !== 'all' ? `&storageLocationId=${selectedStorageLocation}` : '');

  window.open(url, '_blank');
};

  return (
    <div style={{ maxWidth: '1100px', margin: 'auto' }}>
      <Helmet>
        <title>Inspektionsliste</title>
      </Helmet>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Inspektionsübersicht</h2>

      {/* Filterbereich */}
      <form
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
        onSubmit={e => e.preventDefault()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            whiteSpace: 'nowrap',
          }}
        >
          <label>
            Von:&nbsp;
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{ width: '150px' }}
            />
          </label>
          <label>
            Bis:&nbsp;
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{ width: '150px' }}
            />
          </label>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            textAlign: 'left',
          }}
        >
          <label htmlFor="storageLocationFilter" style={{ margin: 0 }}>Lagerort</label>
          <select
            id="storageLocationFilter"
            value={selectedStorageLocation}
            onChange={e => setSelectedStorageLocation(e.target.value)}
            style={{
              width: '180px',
              minWidth: '180px',
              padding: '4px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              margin: 0,
            }}
          >
            <option value="all">Alle</option>
            {storageLocations.map(location => (
              <option key={location.id} value={location.id}>
                {location.name}{location.isActive === false ? ' (inaktiv)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div style={{ textAlign: 'left' }}>
  <label style={{ display: 'block', margin: 0 }}>Inspektionstypen:</label>
  <div
    style={{
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      gap: '50px',
      padding: '2px 0 2px 0',
      minWidth: '400px',
      maxWidth: '100%',
    }}
  >
    {INSPECTION_TYPES.map(t => (
      <label key={t.key} style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={selectedTypes.includes(t.key)}
          onChange={() => handleInspectionTypeChange(t.key)}
          style={{ marginRight: '20px', marginTop: '12px', transform: 'scale(1.8)', }}
        />
        {t.label}
      </label>
    ))}
  </div>
</div>
        <div style={{ textAlign: 'left' }}>
  <label style={{ display: 'block', margin: 0 }}>Artikeltypen:</label>
  <div
    style={{
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      gap: '50px',
      padding: '2px 0 2px 0',
      minWidth: '400px', // nach Wunsch anpassen
      maxWidth: '100%',
      
    }}
  >
    {ITEM_TYPES.map(t => (
      <label key={t.value} style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={selectedItemTypes.includes(t.value)}
          onChange={() => handleItemTypeChange(t.value)}
          style={{ marginRight: '20px', marginTop: '12px', transform: 'scale(1.8)', }}
        />
        {t.label}
      </label>
    ))}
  </div>
</div>
        <button
          type="button"
          onClick={handleDownloadPDF}
          style={{
            padding: '6px 12px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            height: '36px',
            alignSelf: 'center'
          }}
        >
          PDF herunterladen
        </button>
      </form>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Lade Inspektionen…</p>}

      {!loading && inspections.length === 0 && (
        <p>Keine Inspektionen im gewählten Zeitraum gefunden.</p>
      )}

      {!loading && inspections.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={th}>Prüfung</th>
                <th style={th}>Datum</th>
                <th style={th}>Name</th>
                <th style={th}>Lagerort</th>
                <th style={th}>Typ</th>
                <th style={th}>Hersteller</th>
                <th style={th}>Modell</th>
                <th style={th}>Baujahr</th>
                <th style={th}>Seriennr.</th>
                <th style={th}>Inventarnr.</th>
                <th style={th}>LW-ID</th>
                <th style={th}>Kennzeichen</th>
              </tr>
            </thead>
            <tbody>
              {inspections.map((insp, i) => (
                <tr key={insp.itemId + insp.inspectionType + insp.inspectionDate + i}>
                  <td style={td}>{insp.inspectionType}</td>
                  <td style={td}>{new Date(insp.inspectionDate).toLocaleDateString('de-DE')}</td>
                  <td style={td}>{insp.name}</td>
                  <td style={td}>{insp.storageLocation?.name || '-'}</td>
                  <td style={td}>{insp.itemType}</td>
                  <td style={td}>{insp.manufacturer || '-'}</td>
                  <td style={td}>{insp.manufacturerType || '-'}</td>
                  <td style={td}>{insp.yearOfManufacture || '-'}</td>
                  <td style={td}>{insp.serialNumber || '-'}</td>
                  <td style={td}>{insp.inventoryNumber || '-'}</td>
                  <td style={td}>{insp.liftworkerId || '-'}</td>
                  <td style={td}>{insp.licensePlate || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' };
const td = { padding: '8px', border: '1px solid #ddd' };

export default InspectionForm;
